import React, { createContext, useState, useContext, useEffect } from 'react';
import { base44 } from '@/api/base44Client';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Preserve SDK sessions and unrelated page state across navigation.
    checkAppState();
  }, []);

  const checkAppState = () => {
    try {
      const savedUser = localStorage.getItem('jotunheims_user');
      const savedProfile = localStorage.getItem('jotunheims_profile');

      if (savedUser && savedProfile && localStorage.getItem('jotunheims_session_token')) {
        const userData = JSON.parse(savedUser);
        const profileData = JSON.parse(savedProfile);
        setUser(userData);
        setProfile(profileData);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (e) {
      localStorage.removeItem('jotunheims_user');
      localStorage.removeItem('jotunheims_profile');
      localStorage.removeItem('jotunheims_session_token');
      setIsAuthenticated(false);
    } finally {
      setIsLoadingAuth(false);
      setAuthChecked(true);
    }
  };

  const logout = () => {
    if (profile?.id && profile?.user_id) {
      base44.functions.invoke('updateMyProfile', {
        session_token: localStorage.getItem('jotunheims_session_token'),
        profile_id: profile.id,
        data: { is_online: false }
      }).catch(() => {});
    }
    localStorage.removeItem('jotunheims_user');
    localStorage.removeItem('jotunheims_profile');
    localStorage.removeItem('jotunheims_session_token');
    setUser(null);
    setProfile(null);
    setIsAuthenticated(false);
    window.location.href = '/';
  };

  const navigateToLogin = () => {
    window.location.href = '/login';
  };

  // Confirm persistence before showing success or replacing the saved profile.
  const updateProfile = async (data) => {
    if (!profile?.id || !profile?.user_id) throw new Error('Please sign in again.');
    const response = await base44.functions.invoke('updateMyProfile', {
      session_token: localStorage.getItem('jotunheims_session_token'),
      profile_id: profile.id,
      data
    });
    if (!response.data?.success || !response.data.profile) {
      throw new Error(response.data?.error || 'Failed to save profile.');
    }
    const { password_hash, ...updatedProfile } = response.data.profile;
    localStorage.setItem('jotunheims_profile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    return updatedProfile;
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings: false,
      authError: null,
      appPublicSettings: null,
      authChecked,
      logout,
      navigateToLogin,
      checkAppState,
      updateProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};